import React, { useMemo, useState, useCallback, useEffect } from "react";
import { GeographicData } from "../types";
import { getApiKey, getApiBaseUrl } from '../utils/config';
import usFlagImage from "../logoimages/Flag-United-States-of-America.png";

interface GeographicComparisonProps {
  socCode?: string;
  jobTitle?: string;
  geographicData?: GeographicData[];
}

interface JobComparisonData {
  soc_code: string;
  description: {
    country: string;
    state: string;
    county: string;
  };
  employment: {
    country: {
      "2023": number;
      "2024": number;
      "2025": number;
    };
    state: {
      "2023": number;
      "2024": number;
      "2025": number;
    };
    county: {
      "2023": number;
      "2024": number;
      "2025": number;
    };
  };
  salary: {
    country: {
      annual: number;
      hourly: number;
    };
    state: {
      annual: number;
      hourly: number;
    };
    county: {
      annual: number;
      hourly: number;
    };
  };
  openings: {
    country: number;
    state: number;
    county: number;
  };
}

export const GeographicComparison: React.FC<GeographicComparisonProps> = ({
  socCode,
  jobTitle,
  geographicData = [],
}) => {
  // === Hooks must be called first, before any early returns ===
  type MetricKey = "jobs2025" | "medianAnnualSalary" | "averageAnnualOpenings" | "compare";
  const [metric, setMetric] = useState<MetricKey>("medianAnnualSalary");
  const [comparisonData, setComparisonData] = useState<JobComparisonData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // === Fetch real API data ===
  const fetchComparisonData = useCallback(async () => {
    if (!socCode) return;
    
    setLoading(true);
    setError(null);
    try {
      const apiKey = await getApiKey();
      const apiBaseUrl = await getApiBaseUrl();
      const headers = {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(`${apiBaseUrl}/api/comparison/occupation/${socCode}`, { headers });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        setComparisonData(result.data);
      } else {
        throw new Error('No comparison data available');
      }
    } catch (error) {
      console.error('Error fetching comparison data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load comparison data');
    } finally {
      setLoading(false);
    }
  }, [socCode]);

  useEffect(() => {
    if (socCode) {
      fetchComparisonData();
    }
  }, [socCode, fetchComparisonData]);

  // === Convert API data to GeographicData format ===
  const apiGeographicData = useMemo(() => {
    if (!comparisonData) return [];
    
    return [
      {
        name: "United States",
        flag: usFlagImage,
        flagAlt: "US Flag",
        jobs2023: comparisonData.employment.country["2023"],
        jobs2024: comparisonData.employment.country["2024"],
        jobs2025: comparisonData.employment.country["2025"],
        medianAnnualSalary: comparisonData.salary.country.annual,
        medianHourlySalary: comparisonData.salary.country.hourly,
        averageAnnualOpenings: comparisonData.openings.country,
        level: "national" as const
      },
      {
        name: "Tennessee",
        flag: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9e/Flag_of_Tennessee.svg/2560px-Flag_of_Tennessee.svg.png",
        flagAlt: "Tennessee Flag",
        jobs2023: comparisonData.employment.state["2023"],
        jobs2024: comparisonData.employment.state["2024"],
        jobs2025: comparisonData.employment.state["2025"],
        medianAnnualSalary: comparisonData.salary.state.annual,
        medianHourlySalary: comparisonData.salary.state.hourly,
        averageAnnualOpenings: comparisonData.openings.state,
        level: "state" as const
      },
      {
        name: "Sumner County",
        flag: "https://upload.wikimedia.org/wikipedia/en/e/eb/Flag_of_Sumner_County%2C_Tennessee.png",
        flagAlt: "Sumner County Flag",
        jobs2023: comparisonData.employment.county["2023"],
        jobs2024: comparisonData.employment.county["2024"],
        jobs2025: comparisonData.employment.county["2025"],
        medianAnnualSalary: comparisonData.salary.county.annual,
        medianHourlySalary: comparisonData.salary.county.hourly,
        averageAnnualOpenings: comparisonData.openings.county,
        level: "county" as const
      }
    ];
  }, [comparisonData]);

  // === Use API data if available, otherwise use props ===
  const finalGeographicData = apiGeographicData.length > 0 ? apiGeographicData : geographicData;

  // === Helpers ===
  const formatNumber = (num: number): string => {
    if (num >= 1_000_000) return Math.round(num / 1_000_000) + "M";
    if (num >= 1_000) return Math.round(num / 1_000) + "K";
    return Math.round(num).toLocaleString();
  };
  const formatSalary = (salary: number): string => `$${Math.round(salary).toLocaleString()}`;
  const pct = (part: number, whole: number) => (whole === 0 ? 0 : (part / whole) * 100);
  const growthPct = (from: number, to: number) => {
    if (from === 0 || from === null || from === undefined) return 0;
    if (to === null || to === undefined) return 0;
    const change = ((to - from) / from) * 100;
    // Cap extreme growth percentages to prevent anomalies
    return Math.max(-99, Math.min(99, change));
  };

  // === Derived values ===
  const national = useMemo(() => finalGeographicData.find((d) => d.level === "national"), [finalGeographicData]);
  const state = useMemo(() => finalGeographicData.find((d) => d.level === "state"), [finalGeographicData]);
  const county = useMemo(() => finalGeographicData.find((d) => d.level === "county"), [finalGeographicData]);

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-[#e6e6e6] p-6 shadow-lg mt-6">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1b2c5a]"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-2xl border border-[#e6e6e6] p-6 shadow-lg mt-6">
        <div className="text-center text-red-600 py-4">
          <p>Error: {error}</p>
          <button
            onClick={fetchComparisonData}
            className="mt-2 px-4 py-2 bg-[#1b2c5a] text-white rounded-md hover:bg-[#2a3f7a]"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Early return if no data
  if (!national || !state || !county) {
    return (
      <div className="bg-white rounded-2xl border border-[#e6e6e6] p-6 shadow-lg mt-6">
        <div className="text-center py-8">
          <div className="text-[#d5a32a] mb-4">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h3 className="text-lg font-semibold mb-2 text-[#1b2c5a]">Geographic Data Unavailable</h3>
            <p className="text-sm text-[#707070]">
              Geographic employment comparison data is not currently available for this occupation.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const countyShareOfState = Math.round(pct(county.jobs2025, state.jobs2025) * 100) / 100; // Round to 2 decimal places
  const countyShareOfNation = Math.round(pct(county.jobs2025, national.jobs2025) * 10000) / 10000; // Keep 4 decimal places for very small percentages
  const countyGrowth = Math.round(growthPct(county.jobs2023, county.jobs2025));
  const stateGrowth = Math.round(growthPct(state.jobs2023, state.jobs2025));
  const nationalGrowth = Math.round(growthPct(national.jobs2023, national.jobs2025));

  const getMetricLabel = (m: MetricKey) =>
    m === "jobs2025" ? "2025 Jobs" : 
    m === "medianAnnualSalary" ? "Annual Salary Range" : 
    m === "compare" ? "Comparison" : 
    "Avg Annual Openings";

  const getMetricValue = (d: GeographicData) =>
    metric === "medianAnnualSalary"
      ? formatSalary(d.medianAnnualSalary)
      : metric === "compare"
      ? `${Math.round((d.jobs2025 / national.jobs2025) * 10000) / 100}%`
      : formatNumber(metric === "jobs2025" ? d.jobs2025 : d.averageAnnualOpenings);

  const getBarWidth = (d: GeographicData) => {
    if (d.level === "national") return "100%";
    
    let percentage: number;
    if (metric === "compare") {
      percentage = (d.jobs2025 / national.jobs2025) * 100;
    } else {
      const metricValue = d[metric] as number;
      const nationalValue = national[metric] as number;
      if (nationalValue === 0 || nationalValue === null || nationalValue === undefined) return "0%";
      percentage = (metricValue / nationalValue) * 100;
    }
    
    // Ensure minimum visible bar width for very small percentages
    const minWidth = 0.5; // 0.5% minimum width
    const maxWidth = 100; // Cap at 100%
    return `${Math.max(minWidth, Math.min(maxWidth, percentage))}%`;
  };

  const getAriaValue = (d: GeographicData): number => {
    if (d.level === "national") return 100;
    
    let percentage: number;
    if (metric === "compare") {
      if (national.jobs2025 === 0 || national.jobs2025 === null || national.jobs2025 === undefined) return 0;
      percentage = (d.jobs2025 / national.jobs2025) * 100;
    } else {
      const metricValue = d[metric] as number;
      const nationalValue = national[metric] as number;
      if (nationalValue === 0 || nationalValue === null || nationalValue === undefined) return 0;
      percentage = (metricValue / nationalValue) * 100;
    }
    
    // Cap extreme values to prevent accessibility issues
    return Math.max(0, Math.min(100, percentage));
  };

  // === Visual tokens - White Backgrounds ===
  const toneByLevel: Record<GeographicData["level"], string> = {
    national: "bg-white", // White background
    state: "bg-white", // White background
    county: "bg-white", // White background
  };

  const Card: React.FC<{ data: GeographicData; featured?: boolean }> = ({ data, featured }) => (
    <div
      className={`relative ${toneByLevel[data.level]} p-6 rounded-2xl text-[#1b2c5a] shadow-lg border border-[#e6e6e6] ${
        featured ? "ring-4 ring-[#76b6f2]/40 scale-[1.02]" : ""
      }`}
    >
      {/* Ribbon for county */}
      {data.level === "county" && (
        <div className="absolute -top-3 right-4 bg-[#76b6f2] text-white text-xs font-bold px-3 py-1 rounded-full shadow">
          YOUR AREA
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold flex items-center">
          <img alt={data.flagAlt} className="w-6 h-4 mr-2 object-cover rounded" src={data.flag} />
          {data.name}
        </h3>
        <span className="px-2 py-1 bg-[#e6e6e6] text-[#1b2c5a] rounded-full text-[10px] font-bold uppercase tracking-wide">
          {data.level}
        </span>
      </div>

             <div className="space-y-4">
         {/* Jobs triplet */}
         <div className="grid grid-cols-3 gap-3">
           {["2023", "2024", "2025"].map((yr, i) => {
             const v = (data as any)[`jobs${yr}`] as number;
             return (
               <div key={yr} className="bg-[#f8f9fa] p-3 rounded-lg text-center border border-[#e6e6e6]">
                 <div className="text-lg font-bold text-[#1b2c5a]">{formatNumber(v)}</div>
                 <div className="text-xs text-[#707070]">{yr} Jobs</div>
               </div>
             );
           })}
         </div>

         {/* Pay + Hourly */}
         <div className="bg-[#f8f9fa] p-4 rounded-lg border border-[#e6e6e6]">
           <div className="grid grid-cols-2 gap-4">
             <div className="text-center">
               <div className="text-lg font-bold text-[#1b2c5a]">{formatSalary(data.medianAnnualSalary)}</div>
               <div className="text-xs text-[#707070]">Median Annual</div>
             </div>
                          <div className="text-center">
                <div className="text-lg font-bold text-[#1b2c5a]">${Math.round(data.medianHourlySalary)}</div>
                <div className="text-xs text-[#707070]">Median Hourly</div>
              </div>
           </div>
         </div>

         {/* Openings */}
         <div className="bg-[#f8f9fa] p-4 rounded-lg text-center border border-[#e6e6e6]">
           <div className="text-xl font-bold text-[#1b2c5a]">{formatNumber(data.averageAnnualOpenings)}</div>
           <div className="text-xs text-[#707070]">Annual Openings</div>
         </div>

        {/* Growth badge */}
        <GrowthBadge data={data} />
      </div>
    </div>
  );

           const GrowthBadge: React.FC<{ data: GeographicData }> = ({ data }) => {
      const g = Math.round(growthPct(data.jobs2023, data.jobs2025));
      const up = g >= 0;
      return (
        <div className={`flex items-center justify-center text-xs font-semibold rounded-md px-3 py-2 border ${up ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
          <span className={`mr-2 ${up ? "text-emerald-700" : "text-rose-700"}`}>
            {up ? "▲" : "▼"}
          </span>
          <span className={`${up ? "text-emerald-700" : "text-rose-700"}`}>{g}% growth 2023→2025</span>
        </div>
      );
    };

  return (
    <div className="bg-white rounded-2xl border border-[#e6e6e6] p-6 shadow-lg mt-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center mb-2">
          <h2 className="text-2xl font-bold text-[#1b2c5a] mb-2">Geographic Employment Comparison</h2>
          {jobTitle && (
            <p className="text-[#707070] text-sm">
              {jobTitle} {socCode ? `• SOC Code: ${socCode}` : ""}
            </p>
          )}
        </div>

                 {/* Quick brag row that makes county look great - White Backgrounds */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-white border border-[#e6e6e6] p-4 rounded-xl text-[#1b2c5a]">
              <div className="text-xs font-semibold opacity-70">County share of state jobs (2025)</div>
              <div className="text-2xl font-extrabold mt-1">
                {countyShareOfState > 0.01 ? countyShareOfState.toFixed(2) : '<0.01'}%
              </div>
              <div className="text-xs mt-1 opacity-80">
                {countyShareOfState >= 1 ? "Strong local presence" : countyShareOfState >= 0.1 ? "Moderate local presence" : "Growing local opportunity"}
              </div>
            </div>
            <div className="bg-white border border-[#e6e6e6] p-4 rounded-xl text-[#1b2c5a]">
              <div className="text-xs font-semibold opacity-70">County growth trajectory (2023→2025)</div>
              <div className="text-2xl font-extrabold mt-1">
                {countyGrowth}%
                <span className="text-sm font-bold ml-1">growth</span>
              </div>
              <div className="text-xs mt-1 opacity-80">
                {countyGrowth >= stateGrowth ? "Outpacing state average" : "Stable local growth"}
              </div>
            </div>

         </div>

        {/* Comparison Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card data={national} />
          <Card data={state} />
          <Card data={county} featured />
        </div>

                 {/* Metric Toggle + Scales - White Background - Reduced Size & Centered */}
         <div className="bg-white p-4 rounded-xl border border-[#e6e6e6] max-w-2xl mx-auto">
           <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
             <h3 className="text-lg font-bold text-[#1b2c5a]">Market Scale Visualization</h3>
             <div className="flex flex-wrap items-center gap-1">
               <span className="text-xs text-[#1b2c5a] font-semibold">Compare by:</span>
               {(
                 [
                   { key: "jobs2025", label: "2025 Jobs" },
                   { key: "medianAnnualSalary", label: "Median Salary" },
                   { key: "averageAnnualOpenings", label: "Openings" },
                   { key: "compare", label: "Compare" },
                 ] as { key: MetricKey; label: string }[]
               ).map((b) => (
                 <button
                   key={b.key}
                   onClick={() => setMetric(b.key)}
                   className={`px-2 py-1 text-xs rounded-full border transition whitespace-nowrap ${
                     metric === b.key
                       ? "bg-[#1b2c5a] text-white border-[#1b2c5a]"
                       : "bg-white text-[#1b2c5a] border-[#e6e6e6] hover:bg-[#f8f9fa]"
                   }`}
                 >
                   {b.label}
                 </button>
               ))}
             </div>
           </div>

          <div className="space-y-3">
            {finalGeographicData.map((data, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-24 text-sm font-bold text-[#1b2c5a] flex items-center">
                  <img alt={data.flagAlt} className="w-4 h-3 mr-2 object-cover rounded" src={data.flag} />
                  <span className="truncate">{data.name}</span>
                </div>
                                 <div className="flex-1 bg-white rounded-full h-6 shadow-inner border border-[#e6e6e6] overflow-hidden">
                   <div
                     className={`h-6 rounded-full flex items-center justify-between px-2 transition-all duration-700 ease-out ${
                       data.level === "national" 
                         ? "bg-gradient-to-r from-[#1b2c5a] to-[#162844]" 
                         : data.level === "state" 
                         ? "bg-gradient-to-r from-[#d5a32a] to-[#f2c94c]" 
                         : "bg-gradient-to-r from-[#76b6f2] to-[#c4d7f2]"
                     }`}
                     style={{ width: getBarWidth(data) }}
                     aria-valuenow={getAriaValue(data)}
                     aria-valuemin={0}
                     aria-valuemax={100}
                     role="progressbar"
                   >
                     <span className="text-white text-[10px] font-bold drop-shadow">
                       {getMetricLabel(metric)}
                     </span>
                     <span className="text-white text-[10px] font-bold drop-shadow">{getMetricValue(data)}</span>
                   </div>
                 </div>
                <div className="w-20 text-right">
                  <div className="text-sm font-bold text-[#1b2c5a]">{getMetricValue(data)}</div>
                  <div className="text-[10px] text-[#707070]">{getMetricLabel(metric)}</div>
                </div>
              </div>
            ))}
          </div>

                     {/* Legend - White Background - Reduced */}
           <div className="mt-4 pt-3 border-t border-[#e6e6e6]">
             <div className="grid grid-cols-3 gap-3 text-center text-xs">
               <div className="flex items-center justify-center gap-1">
                 <div className="w-2 h-2 bg-[#1b2c5a] rounded"></div>
                 <span className="text-[#1b2c5a] font-medium">National</span>
               </div>
               <div className="flex items-center justify-center gap-1">
                 <div className="w-2 h-2 bg-[#d5a32a] rounded"></div>
                 <span className="text-[#1b2c5a] font-medium">State</span>
               </div>
               <div className="flex items-center justify-center gap-1">
                 <div className="w-2 h-2 bg-[#76b6f2] rounded"></div>
                 <span className="text-[#1b2c5a] font-medium">County</span>
               </div>
             </div>
           </div>
        </div>

        {/* How it stacks up section (explicit comparison bullets) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                     {/* vs National */}
           <div className="rounded-xl border border-[#e6e6e6] p-4">
             <div className="text-xs font-bold text-[#1b2c5a] uppercase tracking-wide mb-2">County vs U.S.</div>
             <ul className="text-sm space-y-2">
               <li>
                 <span className="font-semibold">Jobs share:</span> {countyShareOfNation > 0.0001 ? countyShareOfNation.toFixed(4) : '<0.0001'}% of U.S.
               </li>
               <li>
                 <span className="font-semibold">Growth pace:</span> {countyGrowth}% vs {nationalGrowth}% U.S.
               </li>
                               <li>
                  <span className="font-semibold">Median pay:</span> ${Math.round(county.medianAnnualSalary).toLocaleString()} vs ${
                    Math.round(national.medianAnnualSalary).toLocaleString()
                  }
                </li>
             </ul>
           </div>
           {/* vs State */}
           <div className="rounded-xl border border-[#e6e6e6] p-4">
             <div className="text-xs font-bold text-[#1b2c5a] uppercase tracking-wide mb-2">County vs State</div>
             <ul className="text-sm space-y-2">
               <li>
                 <span className="font-semibold">Jobs share:</span> {countyShareOfState > 0.01 ? countyShareOfState.toFixed(2) : '<0.01'}% of state jobs
               </li>
               <li>
                 <span className="font-semibold">Growth pace:</span> {countyGrowth}% vs {stateGrowth}% state
               </li>
               <li>
                 <span className="font-semibold">Openings:</span> {formatNumber(county.averageAnnualOpenings)} vs {formatNumber(
                   state.averageAnnualOpenings
                 )}
               </li>
             </ul>
           </div>
                       {/* County highlight - White Background */}
            <div className="rounded-xl border border-[#e6e6e6] bg-white p-4">
              <div className="text-xs font-bold text-[#1b2c5a] uppercase tracking-wide mb-2">County highlight</div>
             <ul className="text-sm space-y-2">
               <li>
                 <span className="font-semibold">Growth momentum:</span> {countyGrowth >= stateGrowth ? "Outpacing" : "Maintaining"} state growth
               </li>
               <li>
                 <span className="font-semibold">Market position:</span> {county.medianAnnualSalary >= national.medianAnnualSalary ? "Above" : "Competitive"} U.S. median
               </li>
               <li>
                 <span className="font-semibold">Local impact:</span> {countyShareOfState > 0.01 ? countyShareOfState.toFixed(2) : '<0.01'}% of statewide jobs in this role
               </li>
             </ul>
           </div>
        </div>
      </div>
    </div>
  );
};

export default GeographicComparison;
